import dayjs from 'dayjs';
import { ETransactionPaymentStatuses } from 'src/interfaces/transactions';
import { filterProp } from 'src/interfaces/transactionsFilters';

/**
 * Estados validos en la URL. Son los canonicos del backend, los mismos que
 * ofrece el panel y que compara la tabla: asi un link compartido con un filtro
 * de estado se rehidrata en vez de descartarse.
 */
export const ALLOWED_STATUS = new Set<string>(Object.values(ETransactionPaymentStatuses));

const STRING_MAX_LENGTH = 64;

/**
 * Combos exactos de Monto representables por los buckets del panel (H4).
 * Cualquier otro min/max en la URL se descarta (ajuste 9 post-auditoría).
 */
const AMOUNT_BUCKET_COMBOS: { amount_min: number | null; amount_max: number | null }[] = [
  { amount_min: null, amount_max: 1000 },
  { amount_min: 1000, amount_max: 5000 },
  { amount_min: 5000, amount_max: null }
];

const FILTER_KEYS: (keyof filterProp)[] = [
  'from',
  'to',
  'status',
  'card_type',
  'card_brand',
  'bank',
  'promo',
  'amount_min',
  'amount_max'
];

const emptyFilter = (): filterProp => ({
  from: null,
  to: null,
  status: null,
  card_type: null,
  card_brand: null,
  bank: null,
  promo: null,
  amount_min: null,
  amount_max: null
});

const toPositiveNumber = (value: string): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const sanitizeString = (value: string | null): string | null => {
  const trimmed = value?.trim();
  return trimmed && trimmed.length <= STRING_MAX_LENGTH ? trimmed : null;
};

/**
 * Parsea filtros desde la URL con whitelist y validación (mitigación R4 del threat model):
 * ignora params desconocidos, valida fechas ISO (rango libre — H4), estados del enum,
 * strings acotados (los catálogos canónicos de card_type/bank/brand son del backend),
 * sin terminal_id ni merchant_cuit (H4 no los incluye como filtros del panel),
 * promo ∈ {true,false} y montos solo como combos exactos de bucket.
 * merchant_cuit ya no viaja por URL (el scopeo real es por token en el BFF).
 */
export const parseFiltersFromParams = (params: URLSearchParams): filterProp => {
  const filter = emptyFilter();

  const from = params.get('from');
  const to = params.get('to');
  if (from && dayjs(from).isValid()) filter.from = dayjs(from).toISOString();
  if (to && dayjs(to).isValid()) filter.to = dayjs(to).toISOString();

  filter.card_type = sanitizeString(params.get('card_type'));
  filter.card_brand = sanitizeString(params.get('card_brand'));
  filter.bank = sanitizeString(params.get('bank'));

  const status = params.get('status');
  if (status) {
    const validStatus = status.split(',').filter((value) => ALLOWED_STATUS.has(value));
    if (validStatus.length) filter.status = validStatus.join(',');
  }

  const promo = params.get('promo');
  if (promo === 'true' || promo === 'false') filter.promo = promo;

  const amountMin = params.get('amount_min') ? toPositiveNumber(params.get('amount_min') as string) : null;
  const amountMax = params.get('amount_max') ? toPositiveNumber(params.get('amount_max') as string) : null;
  const matchesBucket = AMOUNT_BUCKET_COMBOS.some(
    (combo) => combo.amount_min === amountMin && combo.amount_max === amountMax
  );
  if (matchesBucket) {
    filter.amount_min = amountMin;
    filter.amount_max = amountMax;
  }

  return filter;
};

/** Serializa el filtro aplicado a query params, omitiendo valores vacíos. */
export const serializeFiltersToParams = (filter: filterProp): URLSearchParams => {
  const params = new URLSearchParams();
  FILTER_KEYS.forEach((key) => {
    const value = filter[key];
    if (value !== null && value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  });
  return params;
};

/** True si el filtro tiene al menos un valor aplicado. */
export const hasAnyFilterValue = (filter: filterProp): boolean =>
  FILTER_KEYS.some((key) => {
    const value = filter[key];
    return value !== null && value !== undefined && value !== '';
  });

/** Igualdad superficial sobre las keys de la capa URL. */
export const filtersAreEqual = (a: filterProp, b: filterProp): boolean =>
  FILTER_KEYS.every((key) => (a[key] ?? null) === (b[key] ?? null));
